import { useState } from 'react';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { ChevronDown, Menu, Globe, LogOut, User as UserIcon } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { useAppSelector, useAppDispatch } from "@/app/store/hooks";
import { logout } from "@/app/store/slices/authSlice";
import { useLogoutMutation } from "@/app/store/api/authApi";
import { Avatar } from "@/components/ui/avatar";
import { getInitials } from "@/lib/utils";
import Tooltip from "@mui/material/Tooltip";
import { WhatsAppIcon } from "@/components/dashboard/socials-dropup";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const MobileMenu = dynamic(
    () => import('./mobile-menu').then(mod => ({ default: mod.MobileMenu })),
    { ssr: false }
);

const LoginPopover = dynamic(
    () => import('../auth/login-popover').then(mod => ({ default: mod.LoginPopover })),
    { ssr: false }
);


const Navbar = () => {
    const { scrollY } = useScroll();
    const router = useRouter();
    const [hidden, setHidden] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isLoginPopoverOpen, setIsLoginPopoverOpen] = useState(false);

    const { user, isAuthenticated } = useAppSelector((state) => state.auth);
    const dispatch = useAppDispatch();
    const [logoutApi] = useLogoutMutation();

    const handleLogout = async () => {
        try {
            await logoutApi({}).unwrap();
        } catch (error) {
            console.error("Logout API failed", error);
        }
        dispatch(logout());
    };

    const initials = getInitials(user?.firstName, user?.lastName);

    // Constant items to be used in both mobile and desktop
    const productItems = [
        { label: "Presentation Builder", path: "/products/presentation-builder" },
        { label: "Fundraising Pitch Deck", path: "/products/fundraising-pitch-deck" },
        { label: "Business Report", path: "/products/business-report" },
        { label: "Cap Table Structure", path: "/products/cap-table-structure" },
        { label: "Partnership Proposal", path: "/products/partnership-proposal-builder" },
        { label: "Team Introduction", path: "/products/team-introduction" }
    ];

    const featureItems = [
        { label: "Deep Research", path: "/features/deep-research" },
        { label: "Presentation Outline", path: "/features/presentation-outline" },
        { label: "AI Content Manipulation", path: "/features/ai-content-manipulation" },
        { label: "Multilingual Presentation", path: "/features/multilingual-presentation" },
        { label: "Brand-Aligned Theme", path: "/features/brand-aligned-theme" },
        { label: "Smart AI Editing", path: "/features/smart-ai-editing" },
        { label: "Image Generation", path: "/features/image-generation" },
        { label: "Collaborative Access", path: "/features/collaborative-access" },
        { label: "Pitch Preparation Q&A", path: "/features/pitch-preparation" },
        { label: "Deep Analytics", path: "/features/deep-analytics" },
        { label: "Multiple Download Formats", path: "/features/multiple-formats" }
    ];

    // Scroll behavior logic
    useMotionValueEvent(scrollY, "change", (latest) => {
        if (isMobileMenuOpen) return; // Don't hide navbar if mobile menu is open
        const diff = latest - lastScrollY;
        if (Math.abs(diff) > 5) { // Threshold to prevent jitter
            if (diff > 0 && latest > 100) {
                setHidden(true); // Scrolling down
            } else {
                setHidden(false); // Scrolling up
            }
        }
        setLastScrollY(latest);
    });

    return (
        <motion.header
            className="fixed top-6 left-0 right-0 z-50 flex justify-center px-4"
            variants={{
                visible: { y: 0, opacity: 1 },
                hidden: { y: "-150%", opacity: 0 }
            }}
            animate={hidden ? "hidden" : "visible"}
            transition={{ duration: 0.3, ease: "easeInOut" }}
        >
            {/* Tweaked: Increased width (max-w-7xl), Condensed padding (px-5 py-2.5) */}
            <nav className="flex items-center justify-between px-6 py-3 rounded-full transition-all duration-300 w-full max-w-6xl bg-white/80 backdrop-blur-xl shadow-2xl shadow-blue-900/20 border border-[#e3ebff] relative">
                {/* Logo */}
                <div className="flex flex-col">
                    <Link href="/" className="font-bold text-xl text-blue-950 tracking-tight leading-none">
                        Deckwale
                    </Link>
                    <span className="text-[10px] font-semibold text-blue-600 leading-none mt-2">by Seedicon</span>
                </div>

                {/* Desktop Menu - Tweaked: Reduced gap (gap-6) */}
                <div className="hidden md:flex items-center gap-6">
                    <ProductsDropdown productItems={productItems} featureItems={featureItems} />

                    {/* Use Cases - Direct Link now */}
                    <Link href="/use-cases" className="text-slate-600 hover:text-blue-700 transition-colors font-medium text-sm">
                        Use Cases
                    </Link>

                    <Link href="/resources" className="text-slate-600 hover:text-blue-700 transition-colors font-medium text-sm">
                        Resources
                    </Link>

                    <Link href="/pricing" className="text-slate-600 hover:text-blue-700 transition-colors font-medium text-sm">
                        Pricing
                    </Link>
                </div>

                {/* Language Selector & CTA Buttons - Tweaked: Gap reduced */}
                <div className="hidden md:flex items-center gap-2">
                    {isAuthenticated ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <div>
                                    <Avatar
                                        className="h-10 w-10 ring-2 ring-white shadow-md cursor-pointer hover:opacity-90 transition-all hover:scale-105"
                                        src={user?.imgUrl}
                                        fallback={initials}
                                        fallbackClassName="bg-blue-600 text-white font-bold text-sm"
                                    />
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56 mt-2">
                                <DropdownMenuLabel className="font-normal">
                                    <div className="flex flex-col space-y-1">
                                        <p className="text-sm font-medium leading-none">{user?.firstName} {user?.lastName}</p>
                                        <p className="text-xs leading-none text-muted-foreground">
                                            {user?.email}
                                        </p>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild className="cursor-pointer">
                                    <Link href="/dashboard">
                                        <UserIcon className="mr-2 h-4 w-4" />
                                        <span>Dashboard</span>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 cursor-pointer">
                                    <LogOut className="mr-2 h-4 w-4" />
                                    <span>Logout</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <>
                            <button
                                onClick={() => setIsLoginPopoverOpen(true)}
                                className="text-blue-900 border border-blue-900/30 hover:border-blue-900 hover:text-blue-700 px-5 py-2 rounded-full text-sm font-bold transition-all hover:bg-blue-50/50"
                            >
                                Login
                            </button>
                            <Link href="/signup" className="bg-blue-600 text-white hover:bg-blue-700 px-5 py-2 rounded-full text-sm font-bold transition-all shadow-md hover:shadow-lg shadow-blue-600/20">
                                Try for free
                            </Link>
                        </>
                    )}
                </div>

                {/* Mobile Auth & Menu Buttons */}
                <div className="md:hidden flex items-center gap-2">
                    {isAuthenticated ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <div>
                                    <Avatar
                                        className="h-8 w-8 ring-2 ring-white shadow-md cursor-pointer hover:opacity-90 transition-all hover:scale-105"
                                        src={user?.imgUrl}
                                        fallback={initials}
                                        fallbackClassName="bg-blue-600 text-white font-bold text-xs"
                                    />
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56 mt-2">
                                <DropdownMenuLabel className="font-normal">
                                    <div className="flex flex-col space-y-1">
                                        <p className="text-sm font-medium leading-none">{user?.firstName} {user?.lastName}</p>
                                        <p className="text-xs leading-none text-muted-foreground">
                                            {user?.email}
                                        </p>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild className="cursor-pointer">
                                    <Link href="/dashboard">
                                        <UserIcon className="mr-2 h-4 w-4" />
                                        <span>Dashboard</span>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 cursor-pointer">
                                    <LogOut className="mr-2 h-4 w-4" />
                                    <span>Logout</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <button
                            onClick={() => router.push('/')}
                            className="text-[11px] font-medium text-blue-900 border border-blue-900/30 hover:text-slate-900 hover:bg-slate-100 border-slate-200 px-3 py-1.5 rounded-full transition-colors"
                        >
                            Login
                        </button>
                    )}
                    <button
                        className="text-blue-900 z-[60] p-2 -mr-2"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                    >
                        {isMobileMenuOpen ? (
                            <motion.div
                                initial={{ rotate: 0 }}
                                animate={{ rotate: 90 }}
                                className="relative w-6 h-6 flex items-center justify-center font-syne font-bold text-2xl"
                            >
                                <span className="absolute">×</span>
                            </motion.div>
                        ) : (
                            <Menu size={24} />
                        )}
                    </button>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            <MobileMenu
                isOpen={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
                productItems={productItems}
                featureItems={featureItems}
                onLoginClick={() => setIsLoginPopoverOpen(true)}
            />

            <LoginPopover
                isOpen={isLoginPopoverOpen}
                onClose={() => setIsLoginPopoverOpen(false)}
            />
        </motion.header>
    );
};


const ScribbleUnderline = () => (
    <motion.svg
        viewBox="0 0 100 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute -bottom-1 left-0 w-full h-2 text-blue-500 pointer-events-none"
        preserveAspectRatio="none"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        exit={{ pathLength: 0, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
    >
        <motion.path
            d="M2 5C15 2 25 8 35 5C45 2 55 8 65 5C75 2 85 8 98 5"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
        />
    </motion.svg>
);

interface NavItem {
    label: string;
    path: string;
}

const ProductsDropdown = ({ productItems, featureItems }: { productItems: NavItem[], featureItems: NavItem[] }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div
            className="relative group h-full flex items-center"
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
        >
            <Link href="/products" className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-blue-700 transition-colors py-2 group-hover:text-blue-900">
                Products
                <ChevronDown size={14} className={`transition-transform duration-200 text-slate-400 group-hover:text-blue-600 ${isOpen ? 'rotate-180' : ''}`} />
            </Link>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                        className="fixed top-[80px] left-1/2 -translate-x-1/2 w-[1050px] z-50 pt-2"
                    >
                        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 ring-1 ring-black/5 overflow-hidden p-6">
                            <div className="grid grid-cols-[220px_1fr_320px] gap-8">

                                {/* Column 1: Products */}
                                <div>
                                    <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3 px-2">Products</h3>
                                    <div className="flex flex-col gap-0.5">
                                        {productItems.map((item) => (
                                            <Link
                                                key={item.label}
                                                href={item.path}
                                                className="relative group/item block px-3 py-2 text-[13.5px] font-medium text-gray-600 hover:text-black transition-colors rounded-lg overflow-hidden whitespace-nowrap"
                                            >
                                                <span className="relative z-10">{item.label}</span>
                                                <div className="absolute inset-0 bg-gray-50 opacity-0 group-hover/item:opacity-100 transition-opacity -z-0 rounded-lg" />
                                                {/* Scribble Effect - Full Width */}
                                                <div className="absolute bottom-1 left-3 right-3 h-2 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300">
                                                    <ScribbleUnderline />
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>

                                {/* Column 2: Features */}
                                <div>
                                    <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3 px-2">Features</h3>
                                    <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
                                        {featureItems.map((item) => (
                                            <Link
                                                key={item.label}
                                                href={item.path}
                                                className="relative group/item block px-3 py-2 text-[13.5px] font-medium text-gray-600 hover:text-black transition-colors rounded-lg whitespace-nowrap"
                                            >
                                                <span className="relative z-10">{item.label}</span>
                                                <div className="absolute inset-0 bg-gray-50 opacity-0 group-hover/item:opacity-100 transition-opacity -z-0 rounded-lg" />
                                                {/* Scribble Effect - Full Width */}
                                                <div className="absolute bottom-1 left-3 right-3 h-2 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300">
                                                    <ScribbleUnderline />
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>

                                {/* Column 3: Product Overview (Visual) */}
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 flex flex-col justify-between relative overflow-hidden group/card cursor-pointer">
                                    <div className="relative z-10">
                                        <h3 className="font-syne font-bold text-base text-slate-900 mb-1.5">Product Overview</h3>
                                        <p className="text-slate-500 text-[13px] leading-relaxed mb-4">
                                            See how Pitch works and explore features
                                        </p>
                                    </div>

                                    {/* Image from Assets */}
                                    <div className="relative mt-auto w-full rounded-lg shadow-lg border border-white/40 overflow-hidden transform group-hover/card:scale-105 transition-transform duration-500">
                                        <Image
                                            src="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1769258595734_wk5zsvcq6h_Fundraising Deck.png"
                                            alt="Product Overview"
                                            fill
                                            className="object-cover w-full h-full"
                                        />
                                    </div>

                                    {/* Link Stretched */}
                                    <Link href="/products" className="absolute inset-0 z-20" aria-label="View Product Overview" />
                                </div>

                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};



const LanguageSelector = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedLang, setSelectedLang] = useState('EN'); // Short Label

    // const languages = [
    //     { code: 'EN', label: 'English' },
    //     { code: 'HI', label: 'Hindi' },
    //     { code: 'FR', label: 'French' },
    //     { code: 'ES', label: 'Spanish' },
    //     { code: 'DE', label: 'German' }
    // ];

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-bold text-slate-600 hover:text-blue-900 transition-colors"
                aria-label="Select Language"
            >
                <Globe size={16} strokeWidth={2.5} />
                <span>{selectedLang}</span>
            </button>

            {/* <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full right-0 mt-2 w-32 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden py-1 z-50"
                    >
                        {languages.map((lang) => (
                            <button
                                key={lang.code}
                                onClick={() => {
                                    setSelectedLang(lang.code);
                                    setIsOpen(false);
                                }}
                                className={`w-full text-left px-4 py-2 text-xs font-bold transition-colors hover:bg-blue-50 flex items-center justify-between ${selectedLang === lang.code ? 'text-blue-600 bg-blue-50/50' : 'text-gray-600'
                                    }`}
                            >
                                {lang.code}
                                {selectedLang === lang.code && <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence> */}
        </div>
    );
};

export default Navbar;
